import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Modal, Button, Header, Divider } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import CloseableModal from 'components/common/CloseableModal';
import { DEFAULT_SUGGESTION_SET_TITLE } from 'components/modules/content/constants';
import { useContentSuggestions } from 'components/modules/content/hooks';
import { ContentSuggestionSetType } from 'components/modules/content/types';
import { MultiTextField } from 'components/modules/fields/components';
import {
  MARKDOWN_FIELD_TYPE,
  TEXT_FIELD_TYPE,
} from 'components/modules/fields/components/MultiTextField/MultiTextField.constants';
import { DismissibleMessage } from 'components/modules/utils/components';
import useSemanticUiProps from 'hooks/useSemanticUiProps';
import { isAdminUser } from 'libs/auth';
import { selectUser } from 'selectors/user';

import {
  createContentSuggestionState,
  approveSuggestion,
  rejectSuggestion,
  editSuggestionReason,
  validateSuggestionReviewStatus,
  editSuggestionAcceptedValue,
  isAnySuggestionReviewed,
} from './ContentSuggestionsModal.utils';
import { ContentSuggestionActions, ContentSuggestionInfoTable } from './components';
import ContentSuggestionReasonInputs from './components/ContentSuggestionReasonInputs';

import './ContentSuggestionsModal.scss';

const propTypes = {
  suggestionSets: PropTypes.arrayOf(ContentSuggestionSetType).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
};

const defaultProps = {
  open: false,
};

function ContentSuggestionsModal({ suggestionSets, onSave, onClose, open, ...modalProps }) {
  const user = useSelector(selectUser);
  const isAdmin = isAdminUser(user);

  const [suggestionReviewStatus, setSuggestionReviewStatus] = useState(
    createContentSuggestionState(suggestionSets),
  );
  const [errors, setErrors] = useState([]);

  const { refreshContentSuggestions, reviewContentSuggestions, loading } = useContentSuggestions({
    fetchOnMount: false,
  });

  const { getModalProps } = useSemanticUiProps();

  function onApproveSuggestion(suggestionId) {
    setSuggestionReviewStatus((prev) => approveSuggestion(prev, suggestionId));
  }

  function onRejectSuggestion(suggestionId) {
    setSuggestionReviewStatus((prev) => rejectSuggestion(prev, suggestionId));
  }

  function onEditSuggestionReason(suggestionId, reason) {
    setSuggestionReviewStatus((prev) => editSuggestionReason(prev, suggestionId, reason));
  }

  function onEditSuggestionAcceptedValue(suggestionId, acceptedValue) {
    setSuggestionReviewStatus((prev) =>
      editSuggestionAcceptedValue(prev, suggestionId, acceptedValue),
    );
  }

  const reviewedSuggestions = isAnySuggestionReviewed(suggestionReviewStatus);

  async function onSaveInternal() {
    const validationErrors = validateSuggestionReviewStatus(suggestionReviewStatus, isAdmin);

    setErrors(validationErrors);

    if (!_isEmpty(validationErrors)) {
      return;
    }

    const [success, result] = await reviewContentSuggestions(suggestionReviewStatus);

    if (!success) {
      setErrors([result]);

      return;
    }

    await refreshContentSuggestions();
    await onSave();

    onClose();
  }

  return (
    <CloseableModal
      {...getModalProps(modalProps)}
      header="Review Content Suggestions"
      size="huge"
      open={open}
      onClose={onClose}
      className="content-suggestions-modal"
    >
      <Modal.Content className="suggestions">
        {suggestionSets.map((suggestionSet, suggestionSetIndex) => {
          const { id: suggestionSetId, originalContent, suggestions, setName } = suggestionSet;
          const { type } = originalContent;

          const suggestionsComponents = suggestions.map((suggestion, index) => {
            const { id: suggestionId, suggestedValue } = suggestion;
            const suggestionStatus = suggestionReviewStatus[suggestionId];
            const { approved, rejected, reason, acceptedValue } = suggestionStatus;

            const isReviewed = approved || rejected;
            const isText = type === TEXT_FIELD_TYPE;

            return (
              <React.Fragment key={suggestionId}>
                {index > 0 && <Divider />}
                <div className="suggestion">
                  <Header as="h4">Content Suggestion</Header>
                  <ContentSuggestionInfoTable suggestion={suggestion} />

                  <Header as="h4">Suggested Content</Header>
                  <div className="suggestion-content">
                    <MultiTextField
                      value={acceptedValue || suggestedValue}
                      originalValue={originalContent.content}
                      fieldType={MARKDOWN_FIELD_TYPE}
                      onChange={(value) => onEditSuggestionAcceptedValue(suggestionId, value)}
                      disableMarkdownToolbar={isText}
                      options={{
                        minHeight: isText ? '80px' : '200px',
                      }}
                    />
                  </div>
                  <div className="suggestion-review-container">
                    <ContentSuggestionReasonInputs
                      suggestionId={suggestionId}
                      reason={reason}
                      onEditSuggestionReason={onEditSuggestionReason}
                      isReviewed={isReviewed}
                      rejected={rejected}
                      approved={approved}
                    />
                    <ContentSuggestionActions
                      approved={approved}
                      rejected={rejected}
                      onApprove={() => onApproveSuggestion(suggestionId)}
                      onReject={() => onRejectSuggestion(suggestionId)}
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          });

          return (
            <div className="suggestion-set" key={suggestionSetId || suggestionSetIndex}>
              <Header as="h3">{setName || DEFAULT_SUGGESTION_SET_TITLE}</Header>
              {suggestionsComponents}
            </div>
          );
        })}
      </Modal.Content>
      <Modal.Actions>
        {!_isEmpty(errors) && (
          <DismissibleMessage
            header="Validation Errors"
            content={errors.join('\n')}
            delay={10}
            initialVisible
            error
            onDismiss={() => setErrors([])}
          />
        )}
        <Button basic className="cancel" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          basic
          className="save"
          onClick={onSaveInternal}
          disabled={loading || !reviewedSuggestions}
          loading={loading}
        >
          Save
        </Button>
      </Modal.Actions>
    </CloseableModal>
  );
}

ContentSuggestionsModal.propTypes = propTypes;
ContentSuggestionsModal.defaultProps = defaultProps;

export default ContentSuggestionsModal;
